import type { StudentKP, LeitnerBox, Subject, PlayerProgress } from './types';

const BOX_INTERVAL_DAYS: Record<LeitnerBox, number> = { 1: 1, 2: 3, 3: 7, 4: 14, 5: 30 };

/**
 * Box → how many *practice papers* until this KP is due again
 * (LEARNING_SYSTEM_DESIGN_v2 §0 #2: 卷次驅動 clock, doubling per box).
 * box1=+1 (next paper), box2=+2, box3=+4, box4=+8, box5=+16.
 */
const BOX_INTERVAL_PAPERS: Record<LeitnerBox, number> = { 1: 1, 2: 2, 3: 4, 4: 8, 5: 16 };

const MAX_RECENT_QUESTIONS = 30;

/**
 * Record a single attempt outcome for a KP.
 * - 'correct'  : promote box (max 5), increment correctCount
 * - 'wrong'    : demote one box (min 1) — LEARNING_SYSTEM_DESIGN_v2 §0 #1:
 *               don't reset to box 1, just step down so the interval can still
 *               grow under a changing question surface
 * - 'careless' : don't change box, increment carelessCount only
 *                (careless mistakes are excluded from mastery denominator)
 */
export function recordAttempt(
  kp: StudentKP,
  outcome: 'correct' | 'wrong' | 'careless',
  nowISO: string,
  questionId?: string,
): void {
  kp.totalAttempts++;
  if (outcome === 'correct') {
    kp.correctCount++;
    kp.box = Math.min(5, kp.box + 1) as LeitnerBox;
  } else if (outcome === 'wrong') {
    kp.box = Math.max(1, kp.box - 1) as LeitnerBox;
  } else {
    // careless: don't punish mastery, don't promote box
    kp.carelessCount++;
  }
  kp.lastReviewedISO = nowISO;
  kp.nextReviewISO = addDays(nowISO, BOX_INTERVAL_DAYS[kp.box]);
  if (questionId) {
    kp.lastSeenQuestionIds.push(questionId);
    if (kp.lastSeenQuestionIds.length > MAX_RECENT_QUESTIONS) {
      kp.lastSeenQuestionIds.shift();
    }
  }
}

/**
 * Advance the paper-count clock by one — call once per practice paper composed
 * (LEARNING_SYSTEM_DESIGN_v2 §0 #2). Treats a missing field as 0 (legacy saves)
 * and returns the new count.
 */
export function advancePracticePaperCount(progress: PlayerProgress): number {
  const next = (progress.practicePaperCount ?? 0) + 1;
  progress.practicePaperCount = next;
  return next;
}

/**
 * Like `recordAttempt`, but ALSO updates the 卷次驅動 due clock
 * (`kp.dueAtPaper`) on top of the legacy date-based `nextReviewISO`
 * (LEARNING_SYSTEM_DESIGN_v2 §0 #2). The new due point is the *current*
 * paper count plus the box's paper interval, so the KP resurfaces after that
 * many future papers. Both review paths stay in sync.
 */
export function recordAttemptV2(
  progress: PlayerProgress,
  kp: StudentKP,
  outcome: 'correct' | 'wrong' | 'careless',
  nowISO: string,
  questionId?: string,
): void {
  recordAttempt(kp, outcome, nowISO, questionId);
  const paperCount = progress.practicePaperCount ?? 0;
  kp.dueAtPaper = paperCount + BOX_INTERVAL_PAPERS[kp.box];
}

/**
 * True when the KP is due under the 卷次驅動 clock: never-scheduled KPs
 * (`dueAtPaper` null/undefined) are always due; otherwise due once enough
 * papers have been composed (LEARNING_SYSTEM_DESIGN_v2 §0 #2).
 */
export function isDueByPaper(kp: StudentKP, progress: PlayerProgress): boolean {
  if (kp.dueAtPaper == null) return true;
  return kp.dueAtPaper <= (progress.practicePaperCount ?? 0);
}

/**
 * Mastery % excluding careless mistakes from the denominator.
 * If every attempt was careless (judged === 0), returns 100 —
 * treat as mastered until proven otherwise.
 */
export function masteryPercent(kp: StudentKP): number {
  if (kp.totalAttempts === 0) return 0;
  const judged = kp.totalAttempts - kp.carelessCount;
  if (judged === 0) return 100;
  return Math.round((kp.correctCount / judged) * 100);
}

/** True when the KP's next review date is on or before nowISO. */
export function isDueToday(kp: StudentKP, nowISO: string): boolean {
  return kp.nextReviewISO <= nowISO;
}

/**
 * Return the existing StudentKP for kpId, or create a fresh one and
 * append it to progress.studentKPs.
 * Idempotent — safe to call multiple times with the same kpId.
 */
export function findOrCreateKP(
  progress: PlayerProgress,
  kpId: string,
  subject: Subject,
  source: StudentKP['source'] = 'prepared',
): StudentKP {
  const existing = progress.studentKPs?.find(k => k.kpId === kpId);
  if (existing) return existing;
  if (!progress.studentKPs) progress.studentKPs = [];
  const fresh: StudentKP = {
    kpId,
    subject,
    totalAttempts: 0,
    correctCount: 0,
    carelessCount: 0,
    lastReviewedISO: '',
    box: 1,
    nextReviewISO: new Date().toISOString(),
    source,
    lastSeenQuestionIds: [],
  };
  progress.studentKPs.push(fresh);
  return fresh;
}

function addDays(iso: string, days: number): string {
  const d = new Date(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString();
}
